import type { MetaFunction, LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData, Link, useFetcher } from "@remix-run/react";
import { useEffect, useState } from "react";
import { HeroSection, type HeroSlide } from "~/components/ui/HeroSection";
import { TodaysMenu } from "~/components/ui/TodaysMenu";
import RecipeCard from "~/components/recipes/RecipeCard";
import { ArticleCard } from "~/components/articles/ArticleCard";
import type { Recipe } from "~/lib/types";
import { useAuth } from "~/lib/auth-context";
import { useRecipeMetadata } from "~/lib/recipe-metadata-context";
import { createSupabaseServerClient } from "~/services/auth.server";
import { db, profiles } from "~/db";
import { eq } from "drizzle-orm";
import { redirect } from "@remix-run/node";

// SEO 관련 import 제거 - 하이드레이션 에러 방지를 위해

export const meta: MetaFunction = () => {
  return [
    { title: "저속노화에 빠지다 | 슬로우다이브" },
    {
      name: "description",
      content:
        "과학적으로 검증된 저속노화 레시피와 건강 정보를 제공합니다. 맞춤형 식단으로 건강한 노화를 시작하세요.",
    },
    { property: "og:title", content: "저속노화에 빠지다 | 슬로우다이브" },
    {
      property: "og:description",
      content:
        "과학적으로 검증된 저속노화 레시피와 건강 정보를 제공합니다. 맞춤형 식단으로 건강한 노화를 시작하세요.",
    },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "저속노화 레시피 - 슬로우다이브" },
    { property: "og:locale", content: "ko_KR" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "저속노화에 빠지다 | 슬로우다이브" },
    {
      name: "twitter:description",
      content: "과학적으로 검증된 저속노화 레시피와 건강 정보를 제공합니다.",
    },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  // OAuth 콜백 처리 (code 파라미터가 있으면)
  const url = new URL(request.url);
  const code = url.searchParams.get("code");

  if (code) {
    const { supabase, headers } = createSupabaseServerClient(request);

    const { data, error } = await supabase.auth.exchangeCodeForSession(code);

    if (error) {
      console.error("OAuth callback error:", error);
      return redirect("/login?error=oauth_error");
    }

    if (data.user) {
      // 사용자 프로필이 존재하는지 확인
      const existingProfile = await db
        .select()
        .from(profiles)
        .where(eq(profiles.profileId, data.user.id))
        .limit(1);

      // 프로필이 없으면 생성
      if (existingProfile.length === 0) {
        try {
          await db.insert(profiles).values({
            profileId: data.user.id,
            email: data.user.email!,
            name:
              data.user.user_metadata?.full_name ||
              data.user.user_metadata?.name ||
              "사용자",
            role: "user",
          });
        } catch (dbError) {
          console.error("Database error creating profile:", dbError);
          // 프로필 생성 실패해도 로그인은 계속 진행
        }
      }
    }

    // 로그인 성공 후 쿼리 파라미터 없이 홈으로 리다이렉트
    return redirect("/", { headers });
  }

  // Hero Section용 슬라이드 데이터
  const heroSlides: HeroSlide[] = [
    {
      id: "1",
      title: "저속노화에 빠지다",
      description: "건강한 식단으로 더 나은 삶을 만들어보세요",
      imageUrl:
        "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1200&h=600&fit=crop",
      ctaText: "레시피 둘러보기",
      ctaLink: "/recipes",
      backgroundColor: "#f8fafc",
    },
    {
      id: "2",
      title: "맞춤형 건강 레시피",
      description: "당신의 건강 상태에 맞는 개인화된 레시피를 제공합니다",
      imageUrl:
        "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1200&h=600&fit=crop",
      ctaText: "추천 받기",
      ctaLink: "/recommendations",
      backgroundColor: "#f0f9ff",
    },
    {
      id: "3",
      title: "과학적으로 검증된 영양정보",
      description: "전문가가 검증한 저속노화 효과가 있는 레시피들",
      imageUrl:
        "https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=1200&h=600&fit=crop",
      ctaText: "건강정보 보기",
      ctaLink: "/articles",
      backgroundColor: "#f0fdf4",
    },
  ];

  return { heroSlides };
}

// Helper function to get category labels
function getCategoryLabel(category: string): string {
  const categoryLabels: Record<string, string> = {
    "slow-aging": "저속노화",
    "health-info": "건강정보",
    ingredients: "식재료정보",
    nutrition: "영양정보",
    lifestyle: "라이프스타일",
  };
  return categoryLabels[category] || category;
}

export default function Index() {
  const { heroSlides } = useLoaderData<typeof loader>();
  const { user } = useAuth();
  const { loadRatings } = useRecipeMetadata();
  const [todaysRecipe, setTodaysRecipe] = useState<Recipe | null>(null);
  const [isLoadingTodaysMenu, setIsLoadingTodaysMenu] = useState(true);

  const randomRecipeFetcher = useFetcher<{ recipe: Recipe | null }>();
  const popularRecipesFetcher = useFetcher<{ recipes: Recipe[] }>();
  const recommendedRecipesFetcher = useFetcher<{ recipes: Recipe[] }>();
  const newRecipesFetcher = useFetcher<{ recipes: Recipe[] }>();
  const latestArticlesFetcher = useFetcher<{ articles: any[] }>();

  // 오늘의 메뉴 로드
  const loadTodaysMenu = () => {
    randomRecipeFetcher.load("/api/recipes/random");
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadTodaysMenu();

    // 인기 레시피 로드
    if (popularRecipesFetcher.state === "idle" && !popularRecipesFetcher.data) {
      popularRecipesFetcher.load("/api/recipes/popular?limit=4");
    }

    // 새로운 레시피 로드
    if (newRecipesFetcher.state === "idle" && !newRecipesFetcher.data) {
      newRecipesFetcher.load("/api/recipes/new?limit=4");
    }

    // 최신 아티클 로드
    if (latestArticlesFetcher.state === "idle" && !latestArticlesFetcher.data) {
      latestArticlesFetcher.load("/api/articles/latest?limit=3");
    }
  }, []);

  // 랜덤 레시피 데이터 업데이트
  useEffect(() => {
    if (randomRecipeFetcher.data) {
      setTodaysRecipe(randomRecipeFetcher.data.recipe);
      setIsLoadingTodaysMenu(false);
    }
  }, [randomRecipeFetcher.data]);

  // 로딩 상태 업데이트
  useEffect(() => {
    setIsLoadingTodaysMenu(randomRecipeFetcher.state === "loading");
  }, [randomRecipeFetcher.state]);

  // 레시피 메타데이터 배치 로드
  useEffect(() => {
    const recipeIds: string[] = [];

    // 오늘의 레시피 ID 추가
    if (todaysRecipe?.id) {
      recipeIds.push(todaysRecipe.id);
    }

    // 인기 레시피 ID들 추가
    if (popularRecipesFetcher.data?.recipes) {
      popularRecipesFetcher.data.recipes.forEach((recipe) => {
        recipeIds.push(recipe.id);
      });
    }

    // 새 레시피 ID들 추가
    if (newRecipesFetcher.data?.recipes) {
      newRecipesFetcher.data.recipes.forEach((recipe) => {
        recipeIds.push(recipe.id);
      });
    }

    // 중복 제거 후 배치 로드
    if (recipeIds.length > 0) {
      const uniqueRecipeIds = [...new Set(recipeIds)];
      loadRatings(uniqueRecipeIds);
    }
  }, [
    todaysRecipe?.id,
    popularRecipesFetcher.data?.recipes,
    newRecipesFetcher.data?.recipes,
  ]);

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <HeroSection
        slides={heroSlides}
        autoPlay={true}
        interval={5000}
        height="lg"
        className="rounded-lg overflow-hidden mt-6"
      />

      {/* Today's Menu Section */}
      <section>
        <TodaysMenu
          recipe={todaysRecipe}
          onRefresh={loadTodaysMenu}
          isLoading={isLoadingTodaysMenu}
        />
      </section>

      {/* Popular Recipes Section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            인기 레시피
          </h2>
          <Link
            to="/recipes?sort=popular"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            전체보기 →
          </Link>
        </div>

        {popularRecipesFetcher.state === "loading" ? (
          <div className="text-center py-8">
            <p className="text-gray-500">로딩 중...</p>
          </div>
        ) : popularRecipesFetcher.data?.recipes?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {popularRecipesFetcher.data.recipes.map((recipe: any) => (
              <RecipeCard
                key={recipe.id}
                id={recipe.id}
                title={recipe.title}
                description={recipe.description}
                imageUrl={recipe.imageUrl || undefined}
                prepTime={recipe.prepTime}
                cookTime={recipe.cookTime}
                difficulty={recipe.difficulty}
                categories={recipe.categories}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">표시할 레시피가 없습니다.</p>
          </div>
        )}
      </section>

      {/* New Recipes Section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            새로 등록된 레시피
          </h2>
          <Link
            to="/recipes?sort=newest"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            전체보기 →
          </Link>
        </div>

        {newRecipesFetcher.state === "loading" ? (
          <div className="text-center py-8">
            <p className="text-gray-500">로딩 중...</p>
          </div>
        ) : newRecipesFetcher.data?.recipes?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {newRecipesFetcher.data.recipes.map((recipe: any) => (
              <RecipeCard
                key={recipe.id}
                id={recipe.id}
                title={recipe.title}
                description={recipe.description}
                imageUrl={recipe.imageUrl || undefined}
                prepTime={recipe.prepTime}
                cookTime={recipe.cookTime}
                difficulty={recipe.difficulty}
                categories={recipe.categories}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">표시할 레시피가 없습니다.</p>
          </div>
        )}
      </section>

      {/* Latest Articles Section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            최신 건강정보
          </h2>
          <Link
            to="/articles"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
          >
            전체보기 →
          </Link>
        </div>

        {latestArticlesFetcher.state === "loading" ? (
          <div className="text-center py-8">
            <p className="text-gray-500">로딩 중...</p>
          </div>
        ) : latestArticlesFetcher.data?.articles?.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {latestArticlesFetcher.data.articles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                categoryLabel={getCategoryLabel(article.category)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">표시할 아티클이 없습니다.</p>
          </div>
        )}
      </section>

      {/* Call to Action for Non-logged Users */}
      {!user && (
        <section className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8 text-center dark:from-blue-900 dark:to-indigo-900">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 dark:text-white">
            더 많은 맞춤 레시피를 원하시나요?
          </h2>
          <p className="text-lg text-gray-600 mb-6 dark:text-gray-400">
            회원가입하고 개인화된 레시피 추천을 받아보세요
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/signup"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium dark:bg-blue-400 dark:hover:bg-blue-500 dark:text-gray-900"
            >
              회원가입
            </Link>
            <Link
              to="/login"
              className="bg-white text-blue-600 border border-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors font-medium dark:bg-blue-400 dark:hover:bg-blue-500 dark:text-gray-900"
            >
              로그인
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
